import _ from 'lodash'
import React, { memo, useEffect, useState, useRef } from 'react'
import { Header, Segment, Container, Grid, Divider } from 'semantic-ui-react'
import {
  Button,
  Form,
  Input,
  Radio,
  Checkbox,
  Dropdown
} from 'formik-semantic-ui'
import { FieldArray } from 'formik'
import {
  requestingInstitutionsForSbu,
  studyDescriptionOptions,
  modalityOptions
} from './optionsData'
import { handleValidate } from './validate'
import { formLabels, referrerLabels, requestLabels } from './labels'
import { TEST_ENVIRONMENT } from './environment'
import produce from 'immer'
import aeTitles from './data/aeTitles.json'

const { subtitle } = formLabels
const styles = {
  buttonGrid: {
    marginTop: '1em',
    paddingBottom: '1em'
  },
  requestSegment: {
    marginBottom: '2em'
  },
  container: {
    marginBottom: '2em'
  }
}

const blankRequest = {
  firstName: '',
  lastName: '',
  dob: '',
  requestedStudyDates: '',
  studyDates: { allDates: null, startDate: '', endDate: '' },
  requestingInstitution: '',
  useInteleViewer: null,
  inteleViewerUsername: '',
  modality: '',
  studyDescription: '',
  businessUnit: '',
  urgent: false
}

const blankReferrer = {
  firstName: '',
  lastName: '',
  providerNumber: '',
  emailAddress: '',
  phoneNumber: ''
}

export const initialValues = {
  referrer: blankReferrer,
  requests: [blankRequest]
}

const RequestFormPart = memo(
  ({ values, setValues, index, remove, push, sourceBusinessUnit }) => {
    const [modality, setModality] = useState('')
    const [studyDescription, setStudyDescription] = useState('')

    const handleModalityInputChange = (e, { value }) => setModality(value)
    const handleStudyDescriptionInputChange = (e, { value }) => {
      setStudyDescription(value)
    }

    // Clear study description if modality changes
    useEffect(() => {
      setValues(
        produce(draft => {
          draft.requests[index].modality = modality
          draft.requests[index].studyDescription = null
        }, values)
      )
    }, [modality])

    // Automatically select the study description if there's only one option
    useEffect(() => {
      const options = studyDescriptionOptions(sourceBusinessUnit, modality)
      if (options.length === 1) {
        setStudyDescription(options[0].value)
        setValues(
          produce(draft => {
            draft.requests[index].studyDescription = options[0].value
          }, values)
        )
      }
    }, [sourceBusinessUnit, modality])
    
    /* Custom search function for the modality dropdown to allow search by both code (label prop) and desc (text prop) */
    const codeSearch = (options, query) => {
      const re = new RegExp(_.escapeRegExp(query), 'i') // ignore case
      return options.filter(
        option => re.test(option.text) || re.test(option.label)
      )
    }
    const {
      allDates: allDatesInitial,
      useInteleViewer: useInteleViewerInitial
    } = values.requests[index]
    const [allDates, setAllDates] = useState(allDatesInitial === 'true')
    const [useInteleViewerChecked, setUseInteleViewerChecked] = useState(
      useInteleViewerInitial
    )

    return (
      <Segment style={{ paddingBottom: '4em' }}>
        <Grid
          columns={2}
          verticalAlign='middle'
          style={{ marginBottom: '1em' }}
        >
          <Grid.Column>
            <Header size='medium'>Request #{index + 1}</Header>
          </Grid.Column>
          <Grid.Column>
            <Button
              disabled={values.requests.length === 1}
              icon='remove'
              title='Remove this request'
              floated='right'
              circular
              onClick={() => remove(index)}
            />
          </Grid.Column>
        </Grid>
        <Form.Group widths='equal'>
          <Input
            fieldProps={{ required: true }}
            label={requestLabels.firstName}
            name={`requests.${index}.firstName`}
          />
          <Input
            fieldProps={{ required: true }}
            label={requestLabels.lastName}
            name={`requests.${index}.lastName`}
          />
        </Form.Group>
        <Input
          fieldProps={{ required: true }}
          inputProps={{ type: 'date' }}
          label={requestLabels.dob}
          name={`requests.${index}.dob`}
        />

        <Divider style={{ margin: '2em 0px' }} />

        <Form.Field
          label={requestLabels.businessUnit}
          name={`requests.${index}.businessUnit`}
          value={sourceBusinessUnit}
          control={'h3'}
          style={{ margin: '0px' }}
        >
          {sourceBusinessUnit}
        </Form.Field>

        {sourceBusinessUnit === 'Apex Radiology (WA)' && (
          <>
            <Form.Group>
              <Form.Field>
                <Radio
                  inputProps={{
                    radio: true,
                    onChange: () => setUseInteleViewerChecked(true)
                  }}
                  value={true}
                  label={requestLabels.useInteleViewer}
                  name={`requests.${index}.useInteleViewer`}
                  errorComponent={message => <></>}
                />
                <Radio
                  inputProps={{
                    radio: true,
                    onChange: () => setUseInteleViewerChecked(false)
                  }}
                  value={false}
                  label={requestLabels.useWachs}
                  name={`requests.${index}.useInteleViewer`}
                  errorComponent={({ message }) => (
                    <div className='sui-error-message'>{message}</div>
                  )}
                />
              </Form.Field>
            </Form.Group>
            {useInteleViewerChecked && (
              <Input
                fieldProps={{
                  required: true
                }}
                inputProps={{
                  placeholder: 'Usernames are case sensitive.'
                }}
                label={requestLabels.inteleViewerUsername}
                name={`requests.${index}.inteleViewerUsername`}
              />
            )}
          </>
        )}

        {sourceBusinessUnit !== 'Apex Radiology (WA)' && (
          <Dropdown
            fieldProps={{ required: true }}
            inputProps={{
              search: true,
              selection: true,
              selectOnBlur: true,
              openOnFocus: false
            }}
            options={requestingInstitutionsForSbu(sourceBusinessUnit)}
            label={requestLabels.requestingInstitution}
            name={`requests.${index}.requestingInstitution`}
          />
        )}

        {/*
      <Form.Field>
        <a href={'#'} role='button'>
          For new referrers, click here to create a ticket.
        </a>
      </Form.Field>
      */}

        <Form.Field
          label={'Requested study dates'}
          required
          name={`requests.${index}.requestedStudyDates`}
          value=''
        />

        <Form.Group>
          <Form.Field>
            <Radio
              inputProps={{
                radio: true,
                onChange: () => setAllDates(true)
              }}
              value={'true'}
              label={'All study dates'}
              name={`requests.${index}.studyDates.allDates`}
              errorComponent={message => <></>}
            />
            <Radio
              inputProps={{
                radio: true,
                onChange: () => setAllDates(false)
              }}
              value={'false'}
              label={'Specific date range...'}
              name={`requests.${index}.studyDates.allDates`}
              errorComponent={({ message }) => (
                <div className='sui-error-message'>{message}</div>
              )}
            />
          </Form.Field>
        </Form.Group>
        {!allDates && (
          <>
            <Form.Group widths='equal'>
              <Input
                fieldProps={{ required: !allDates }}
                inputProps={{ type: 'date' }}
                label={requestLabels.startDate}
                name={`requests.${index}.studyDates.startDate`}
              />
              <Input
                fieldProps={{ required: !allDates }}
                inputProps={{ type: 'date' }}
                label={requestLabels.endDate}
                name={`requests.${index}.studyDates.endDate`}
              />
            </Form.Group>
          </>
        )}

        <Divider style={{ margin: '2em 0px' }} />

        {/* Controlled component for Modality to determine study descriptions */}
        <Dropdown
          fieldProps={{ required: true }}
          inputProps={{
            search: codeSearch,
            selection: true,
            selectOnBlur: true,
            value: modality.value,
            onChange: handleModalityInputChange,
            openOnFocus: false
          }}
          options={modalityOptions(sourceBusinessUnit)}
          label={requestLabels.modality}
          name={`requests.${index}.modality`}
        />
        <Dropdown
          fieldProps={{
            required:
              studyDescriptionOptions(sourceBusinessUnit, modality).length > 0
          }}
          inputProps={{
            search: codeSearch,
            selection: true,
            selectOnBlur: true,
            openOnFocus: false,
            value: studyDescription.value,
            onChange: handleStudyDescriptionInputChange,
          }}
          options={studyDescriptionOptions(sourceBusinessUnit, modality) || []}
          label={requestLabels.studyDescription}
          name={`requests.${index}.studyDescription`}
        />
        <Checkbox
          fieldProps={{ required: false }}
          label={requestLabels.urgent}
          name={`requests.${index}.urgent`}
        ></Checkbox>

        <Button
          basic
          floated='right'
          content='additional request'
          icon='clone'
          title='Additional request for this patient'
          onClick={() => {
            const copy = {
              ...values.requests[index],
              modality: null,
              studyDescription: null,
              urgent: null
            }
            push(copy)
          }}
        />
      </Segment>
    )
  }
)

export const DiagnosticsForm = memo(props => {
  const { formState, setFormState, sourceBusinessUnit } = props

  const handleSubmit = (values, formikApi) => {
    let _values = produce(values => {
      values.requests.forEach(request => {
        if (sourceBusinessUnit === 'Apex Radiology (WA)') {
          if (!request.useInteleViewer) {
            delete request.inteleViewerUsername
          }
        } else {
          delete request.useInteleViewer
          delete request.inteleViewerUsername
        }
      })
    }, values)()
    setFormState(state => ({
      ...state,
      submitted: true,
      submitting: false,
      values: _values
    }))
    formikApi.setSubmitting(true)
  }

  return (
    <>
      <Form
        initialValues={formState.values}
        onSubmit={handleSubmit}
        validate={values =>
          formState.submitting ? handleValidate(values, sourceBusinessUnit) : {}
        }
        validateOnBlur={false}
        validateOnChange={false}
        validateOnMount={false}
        render={({ values, setValues }) => (
          <>
            <Container text style={styles.container}>
              <Container text textAlign='center'>
                {TEST_ENVIRONMENT ? (
                  <strong style={{ color: 'red' }}>Test environment</strong>
                ) : (
                  subtitle
                )}
              </Container>
              <Header dividing size='medium'>
                Referrer/Requestor Information
              </Header>
              <Form.Group widths='equal'>
                <Input
                  fieldProps={{ required: true }}
                  label={referrerLabels.firstName}
                  name='referrer.firstName'
                />
                <Input
                  fieldProps={{ required: true }}
                  label={referrerLabels.lastName}
                  name='referrer.lastName'
                />
              </Form.Group>
              <Input
                fieldProps={{ required: false }}
                label={referrerLabels.providerNumber}
                name='referrer.providerNumber'
              />
              <Input
                fieldProps={{ required: true }}
                inputProps={{ type: 'email' }}
                label={referrerLabels.emailAddress}
                name='referrer.emailAddress'
              />
              <Input
                fieldProps={{ required: true }}
                inputProps={{ type: 'tel' }}
                label={referrerLabels.phoneNumber}
                name='referrer.phoneNumber'
              />
            </Container>

            <Container text style={styles.container}>
              <FieldArray
                name='requests'
                render={({ remove, push }) => (
                  <>
                    <Segment.Group>
                      {values.requests.map((request, index) => (
                        <RequestFormPart
                          key={index}
                          {...{
                            values,
                            request,
                            index,
                            remove,
                            push,
                            setValues,
                            sourceBusinessUnit
                          }}
                        />
                      ))}
                    </Segment.Group>

                    <Grid columns={2} style={styles.buttonGrid}>
                      <Grid.Column>
                        <Button
                          content='Attach another request'
                          icon='add'
                          floated='left'
                          labelPosition='left'
                          onClick={() => push(blankRequest)}
                        />
                      </Grid.Column>
                      <Grid.Column>
                        <Button.Submit
                          onClick={() =>
                            setFormState(state => ({
                              ...state,
                              submitting: true
                            }))
                          }
                          floated='right'
                        >
                          Proceed to review
                        </Button.Submit>
                      </Grid.Column>
                    </Grid>
                  </>
                )}
              />
            </Container>
          </>
        )}
      />
    </>
  )
})
